import React, { useEffect, useState } from 'react';
import { Phone, MapPin, Instagram, Menu as MenuIcon, X, ChevronRight, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ShopSection } from '../components/ShopSection';
import { CartDrawer } from '../components/CartDrawer';
import { useCart } from '../context/CartContext';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { totalItems, setIsOpen } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = {
    dogs: {
      bowls: [
        { name: 'Egg Rice', price: 199 },
        { name: 'Boiled Chicken and Rice', price: 249 },
        { name: 'Chicken Pumpkin Risotto', price: 399 },
        { name: 'Chicken Sweet Potato Risotto', price: 399 },
        { name: 'Chicken Carrot Risotto', price: 399 },
        { name: 'Chicken Pumpkin, Sweet Potato and Carrot Risotto', price: 449 },
        { name: 'Grilled Chicken', price: 299 },
        { name: 'Egg and Vegetable Scramble', price: 299 },
        { name: 'Lamb & Zucchini', price: 499 },
        { name: 'Rice and Vegetable', price: 199 },
        { name: 'Curd Rice', price: 149 }
      ],
      meals: [
        { name: 'Chicken Meal', price: 499 },
        { name: 'Lamb Meal', price: 599 },
        { name: 'Fish Meal', price: 599 },
        { name: 'Egg Meal', price: 399 },
        { name: 'Paneer Meal', price: 399 },
        { name: 'Mini Chicken Meal Sample', price: 199 }
      ],
      broth: [
        { name: 'Chicken Broth Soup', price: 499 },
        { name: 'Lamb Broth', price: 599 },
        { name: 'Fish Broth', price: 599 },
        { name: 'Egg Soup', price: 399 }
      ],
      desserts: [
        { name: 'Apple Pawpuccino', price: 199 },
        { name: 'Banana Pawpuccino', price: 149 }
      ],
      supplements: [
        { name: 'Seed Blend Powder', price: 399 },
        { name: 'Pumpkin Puree (Dogs & Cats)', price: 199 },
        { name: 'Carrot Puree', price: 199 },
        { name: 'Fish Powder', price: 299 },
        { name: 'All In One Dog Supplement', price: 399 }
      ]
    },
    cats: {
      bowls: [
        { name: 'Boiled Chicken', price: 219 },
        { name: 'Chicken and Egg with Rice', price: 299 },
        { name: 'Fish with Rice', price: 499 },
        { name: 'Boiled Chicken with Rice', price: 249 },
        { name: 'Chicken and Pumpkin with Rice', price: 399 },
        { name: 'Chicken Rice with Liver', price: 299 }
      ],
      meals: [
        { name: 'Cat Meal (Chicken, Fish, Liver, Pumpkin, Rice & Egg)', price: 499 }
      ],
      broth: [
        { name: 'Chicken Broth Soup', price: 299 },
        { name: 'Egg Soup', price: 199 },
        { name: 'Fish Broth', price: 399 },
        { name: 'Chicken and Liver Soup', price: 299 }
      ],
      supplements: [
        { name: 'Fish Powder', price: 399 }
      ]
    }
  };

  const services = [
    {
      title: 'Vet on Call',
      description: 'Professional veterinary consultation at your doorstep',
      image: 'https://images.unsplash.com/photo-1764434985496-f18e6309f2a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxwZXQlMjBvd25lciUyMGJvbmRpbmd8ZW58MHx8fHwxNzcwOTY0OTM3fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      title: 'Nutritionist Consultation',
      description: 'Expert guidance on your pet\'s dietary needs',
      image: 'https://images.unsplash.com/photo-1579113800032-c38bd7635818?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMGluZ3JlZGllbnRzfGVufDB8fHx8MTc3MDk2NDk0NHww&ixlib=rb-4.1.0&q=85'
    },
    {
      title: 'Luxury Boarding',
      description: 'Premium care and comfort for your pets',
      image: 'https://images.unsplash.com/photo-1768168211298-e1c0af5cc2cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxwZXQlMjBvd25lciUyMGJvbmRpbmd8ZW58MHx8fHwxNzcwOTY0OTM3fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      title: 'Dehydrated Treats',
      description: 'Healthy, natural treats your pets will love',
      image: 'https://images.unsplash.com/photo-1596854675667-b1716ee49130?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxkb2clMjBlYXRpbmd8ZW58MHx8fHwxNzcwOTY0OTEyfDA&ixlib=rb-4.1.0&q=85'
    },
    {
      title: 'Natural Supplements',
      description: 'Boost your pet\'s health with natural supplements',
      image: 'https://images.unsplash.com/photo-1518843875459-f738682238a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxmcmVzaCUyMGluZ3JlZGllbnRzfGVufDB8fHx8MTc3MDk2NDk0NHww&ixlib=rb-4.1.0&q=85'
    },
    {
      title: 'Pet Grooming',
      description: 'Professional grooming services for your furry friends',
      image: 'https://images.unsplash.com/photo-1534361960057-19889db9621e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHBldHN8ZW58MHx8fHwxNzcwOTY0OTUyfDA&ixlib=rb-4.1.0&q=85'
    }
  ];

  const testimonials = [
    {
      name: 'Priya Sharma',
      text: 'My dog absolutely loves the chicken meals! The quality is exceptional and I can see the difference in his energy levels.',
      rating: 5
    },
    {
      name: 'Rahul Mehta',
      text: 'Fresh, homemade food delivered right to my door. My cat has never been healthier. Highly recommend Furrbowl!',
      rating: 5
    },
    {
      name: 'Ananya Reddy',
      text: 'The monthly subscription is so convenient. No more worrying about pet food quality. Furrbowl is a lifesaver!',
      rating: 5
    }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_26712d7a-6b6e-4dd6-88e7-f2147f408025/artifacts/zr04zw0o_%5BOriginal%20size%5D%20FurrBowl%20%281000%20x%201000%20px%29%20%284000%20x%204000%20px%29%20%281000%20x%20500%20px%29%20%2815%20x%2015%20cm%29%20%28567%20x%20567%20px%29%20%282%29.png" 
                alt="FurrBowl Logo" 
                className="h-16 md:h-20 w-auto"
              />
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-[#1e1919] hover:text-[#61525a] transition-colors">Home</button>
              <button onClick={() => scrollToSection('menu')} className="text-[#1e1919] hover:text-[#61525a] transition-colors">Menu</button>
              <button onClick={() => scrollToSection('shop')} className="text-[#1e1919] hover:text-[#61525a] transition-colors">Shop</button>
              <button onClick={() => scrollToSection('plans')} className="text-[#1e1919] hover:text-[#61525a] transition-colors">Plans</button>
              <button onClick={() => scrollToSection('services')} className="text-[#1e1919] hover:text-[#61525a] transition-colors">Services</button>
              <button onClick={() => scrollToSection('contact')} className="text-[#1e1919] hover:text-[#61525a] transition-colors">Contact</button>
              <button
                onClick={() => setIsOpen(true)}
                className="relative text-[#1e1919] hover:text-[#ff8c19] transition-colors"
                data-testid="header-cart-btn"
                aria-label="Open cart"
              >
                <ShoppingCart size={24} />
                {totalItems > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#ff8c19] text-white text-xs font-bold h-5 w-5 flex items-center justify-center rounded-full" data-testid="cart-count-badge">
                    {totalItems}
                  </span>
                )}
              </button>
            </nav>

            {/* Mobile actions */}
            <div className="md:hidden flex items-center gap-4">
              <button
                onClick={() => setIsOpen(true)}
                className="relative text-[#1e1919] hover:text-[#ff8c19]"
                data-testid="header-cart-btn-mobile"
                aria-label="Open cart"
              >
                <ShoppingCart size={24} />
                {totalItems > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#ff8c19] text-white text-xs font-bold h-5 w-5 flex items-center justify-center rounded-full">
                    {totalItems}
                  </span>
                )}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-[#1e1919] hover:text-[#61525a]"
              >
                {mobileMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-3 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left text-[#1e1919] hover:text-[#61525a] py-2">Home</button>
              <button onClick={() => scrollToSection('menu')} className="block w-full text-left text-[#1e1919] hover:text-[#61525a] py-2">Menu</button>
              <button onClick={() => scrollToSection('shop')} className="block w-full text-left text-[#1e1919] hover:text-[#61525a] py-2">Shop</button>
              <button onClick={() => scrollToSection('plans')} className="block w-full text-left text-[#1e1919] hover:text-[#61525a] py-2">Plans</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-[#1e1919] hover:text-[#61525a] py-2">Services</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-[#1e1919] hover:text-[#61525a] py-2">Contact</button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section - Full Width Background with Overlay */}
      <section id="home" className="relative pt-32 pb-12 px-4 sm:px-6 lg:px-8 min-h-[650px] md:min-h-[750px] lg:min-h-[800px] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0 mt-20">
          <img 
            src="/hero-banner.png" 
            alt="FurrBowl - Fresh Pet Food" 
            className="w-full h-full object-cover object-center"
            style={{ objectPosition: 'center 40%' }}
          />
          {/* Gradient Overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent md:from-black/70 md:via-black/40 md:to-transparent"></div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto w-full relative z-10">
          <div className="max-w-xl md:max-w-2xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 drop-shadow-2xl">
              Fresh Homemade Food & Premium Care for Your Pets
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-white/95 mb-8 drop-shadow-lg max-w-lg">
              Natural, nutritious meals prepared with love for your furry companions. Available on daily, weekly, and monthly subscriptions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => window.open('https://www.swiggy.com/menu/1200378?source=sharing', '_blank')}
                className="bg-[#ff8c19] hover:bg-[#e67d16] text-white px-8 py-6 text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                Order on Swiggy
                <ChevronRight className="ml-2" />
              </Button>
              <Button 
                onClick={() => window.open('https://zomato.onelink.me/xqzv/fx595vjb', '_blank')}
                className="bg-[#b4dc19] hover:bg-[#a2c916] text-[#1e1919] px-8 py-6 text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                Order on Zomato
                <ChevronRight className="ml-2" />
              </Button>
            </div>

            {/* Trust Badge */}
            <div className="mt-8 inline-block">
              <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-4">
                <p className="text-sm text-[#736c64] mb-2">Trusted by 1000+ pet parents</p>
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-[#ff8c19] text-xl">★</span>
                  ))}
                  <span className="ml-2 text-[#1e1919] font-semibold">4.9/5</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1768168211298-e1c0af5cc2cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxwZXQlMjBvd25lciUyMGJvbmRpbmd8ZW58MHx8fHwxNzcwOTY0OTM3fDA&ixlib=rb-4.1.0&q=85" 
                alt="Pet bonding" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-[#1e1919]">About FurrBowl</h2>
              <p className="text-lg text-[#736c64] leading-relaxed">
                At FurrBowl, we believe every pet deserves fresh, nutritious meals made with love. Our expert pet nutritionists craft each recipe using premium, natural ingredients to ensure your furry friends get the best nutrition possible.
              </p>
              <p className="text-lg text-[#736c64] leading-relaxed">
                Whether you're a busy pet parent or simply want the best for your companion, our daily, weekly, and monthly subscription plans make healthy eating convenient and affordable.
              </p>
              <div className="grid grid-cols-3 gap-6 pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#61525a]">100%</div>
                  <div className="text-sm text-[#736c64] mt-1">Natural</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#61525a]">Fresh</div>
                  <div className="text-sm text-[#736c64] mt-1">Daily</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#61525a]">1000+</div>
                  <div className="text-sm text-[#736c64] mt-1">Happy Pets</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f7f5f2]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#1e1919] mb-4">Our Menu</h2>
            <p className="text-lg text-[#736c64]">Delicious, nutritious meals crafted for your pets</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="dog-menu" className="bg-white rounded-2xl shadow-md overflow-hidden border-none">
              <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl font-bold text-[#61525a]">For Dogs</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-semibold text-[#1e1919] mb-4">Bowls</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {menuItems.dogs.bowls.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <span className="text-[#1e1919]">{item.name}</span>
                          <span className="font-semibold text-[#61525a]">₹{item.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-[#1e1919] mb-4">Meals</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {menuItems.dogs.meals.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <span className="text-[#1e1919]">{item.name}</span>
                          <span className="font-semibold text-[#61525a]">₹{item.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-[#1e1919] mb-4">Broth & Soups</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {menuItems.dogs.broth.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <span className="text-[#1e1919]">{item.name}</span>
                          <span className="font-semibold text-[#61525a]">₹{item.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-[#1e1919] mb-4">Desserts</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {menuItems.dogs.desserts.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <span className="text-[#1e1919]">{item.name}</span>
                          <span className="font-semibold text-[#61525a]">₹{item.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-[#1e1919] mb-4">Supplements</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {menuItems.dogs.supplements.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <span className="text-[#1e1919]">{item.name}</span>
                          <span className="font-semibold text-[#61525a]">₹{item.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="cat-menu" className="bg-white rounded-2xl shadow-md overflow-hidden border-none">
              <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl font-bold text-[#61525a]">For Cats</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-semibold text-[#1e1919] mb-4">Bowls</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {menuItems.cats.bowls.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <span className="text-[#1e1919]">{item.name}</span>
                          <span className="font-semibold text-[#61525a]">₹{item.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-[#1e1919] mb-4">Meals</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {menuItems.cats.meals.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <span className="text-[#1e1919]">{item.name}</span>
                          <span className="font-semibold text-[#61525a]">₹{item.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-[#1e1919] mb-4">Broth & Soups</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {menuItems.cats.broth.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <span className="text-[#1e1919]">{item.name}</span>
                          <span className="font-semibold text-[#61525a]">₹{item.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-[#1e1919] mb-4">Supplements</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {menuItems.cats.supplements.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <span className="text-[#1e1919]">{item.name}</span>
                          <span className="font-semibold text-[#61525a]">₹{item.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Shop Section */}
      <ShopSection />

      {/* Subscription Plans */}
      <section id="plans" className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#1e1919] mb-4">Subscription Plans</h2>
            <p className="text-lg text-[#736c64]">Choose the perfect plan for your pet's needs</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-2 border-gray-100">
              <CardContent className="p-8">
                <div className="text-center space-y-4">
                  <h3 className="text-2xl font-bold text-[#1e1919]">Daily Plan</h3>
                  <div className="text-4xl font-bold text-[#61525a]">Flexible</div>
                  <p className="text-[#736c64]">Order fresh meals daily</p>
                  <ul className="space-y-3 text-left pt-6">
                    <li className="flex items-start">
                      <span className="text-[#b4dc19] mr-2">✓</span>
                      <span className="text-[#736c64]">Fresh meals every day</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b4dc19] mr-2">✓</span>
                      <span className="text-[#736c64]">No commitment required</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b4dc19] mr-2">✓</span>
                      <span className="text-[#736c64]">Cancel anytime</span>
                    </li>
                  </ul>
                  <Button 
                    onClick={() => window.open('https://www.swiggy.com/menu/1200378?source=sharing', '_blank')}
                    className="w-full mt-6 bg-[#f7f5f2] hover:bg-[#e8e6e3] text-[#1e1919] rounded-full py-6"
                  >
                    Order Now
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-2 border-[#61525a] transform scale-105">
              <CardContent className="p-8">
                <div className="absolute top-0 right-0 bg-[#ff8c19] text-white px-4 py-1 rounded-bl-2xl rounded-tr-2xl text-sm font-semibold">
                  Popular
                </div>
                <div className="text-center space-y-4">
                  <h3 className="text-2xl font-bold text-[#1e1919]">Weekly Plan</h3>
                  <div className="text-4xl font-bold text-[#61525a]">Save 10%</div>
                  <p className="text-[#736c64]">One week of fresh meals</p>
                  <ul className="space-y-3 text-left pt-6">
                    <li className="flex items-start">
                      <span className="text-[#b4dc19] mr-2">✓</span>
                      <span className="text-[#736c64]">7 days of fresh meals</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b4dc19] mr-2">✓</span>
                      <span className="text-[#736c64]">10% discount</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b4dc19] mr-2">✓</span>
                      <span className="text-[#736c64]">Free delivery</span>
                    </li>
                  </ul>
                  <Button 
                    onClick={() => window.open('https://wa.me/918050539777?text=Hi%20Furrbowl!%20I%27m%20interested%20in%20the%20Weekly%20Plan', '_blank')}
                    className="w-full mt-6 bg-[#61525a] hover:bg-[#524349] text-white rounded-full py-6"
                  >
                    Subscribe Now
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-2 border-gray-100">
              <CardContent className="p-8">
                <div className="text-center space-y-4">
                  <h3 className="text-2xl font-bold text-[#1e1919]">Monthly Plan</h3>
                  <div className="text-4xl font-bold text-[#61525a]">Save 20%</div>
                  <p className="text-[#736c64]">Best value for your pet</p>
                  <ul className="space-y-3 text-left pt-6">
                    <li className="flex items-start">
                      <span className="text-[#b4dc19] mr-2">✓</span>
                      <span className="text-[#736c64]">30 days of fresh meals</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b4dc19] mr-2">✓</span>
                      <span className="text-[#736c64]">20% discount</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b4dc19] mr-2">✓</span>
                      <span className="text-[#736c64]">Priority support</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b4dc19] mr-2">✓</span>
                      <span className="text-[#736c64]">Free nutritionist call</span>
                    </li>
                  </ul>
                  <Button 
                    onClick={() => window.open('https://wa.me/918050539777?text=Hi%20Furrbowl!%20I%27m%20interested%20in%20the%20Monthly%20Plan', '_blank')}
                    className="w-full mt-6 bg-[#f7f5f2] hover:bg-[#e8e6e3] text-[#1e1919] rounded-full py-6"
                  >
                    Subscribe Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f7f5f2]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#1e1919] mb-4">Our Services</h2>
            <p className="text-lg text-[#736c64]">Complete care for your beloved pets</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <Card key={idx} className="rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group border-none">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#1e1919] mb-2">{service.title}</h3>
                  <p className="text-[#736c64]">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#1e1919] mb-4">What Pet Parents Say</h2>
            <p className="text-lg text-[#736c64]">Trusted by thousands of happy pet families</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-none">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-[#ff8c19] text-xl">★</span>
                    ))}
                  </div>
                  <p className="text-[#736c64] mb-4 italic">"{testimonial.text}"</p>
                  <p className="font-semibold text-[#1e1919]">- {testimonial.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f7f5f2]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-[#1e1919]">Get In Touch</h2>
              <p className="text-lg text-[#736c64]">
                Have questions? We're here to help! Reach out to us and we'll get back to you as soon as possible.
              </p>
              
              <div className="space-y-4 pt-4">
                <a 
                  href="tel:8050539777" 
                  className="flex items-center space-x-4 p-4 bg-white rounded-xl hover:shadow-md transition-shadow"
                >
                  <div className="bg-[#61525a] p-3 rounded-full">
                    <Phone className="text-white" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold text-[#1e1919]">Call Us</div>
                    <div className="text-[#736c64]">8050539777</div>
                  </div>
                </a>

                <a 
                  href="https://wa.me/918050539777" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-4 p-4 bg-white rounded-xl hover:shadow-md transition-shadow"
                >
                  <div className="bg-[#b4dc19] p-3 rounded-full">
                    <Phone className="text-[#1e1919]" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold text-[#1e1919]">WhatsApp</div>
                    <div className="text-[#736c64]">Chat with us</div>
                  </div>
                </a>

                <a 
                  href="https://www.instagram.com/furrbowl?igsh=aGFnaHFhMXFpcDRk&utm_source=qr" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-4 p-4 bg-white rounded-xl hover:shadow-md transition-shadow"
                >
                  <div className="bg-[#ff8c19] p-3 rounded-full">
                    <Instagram className="text-white" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold text-[#1e1919]">Instagram</div>
                    <div className="text-[#736c64]">@furrbowl</div>
                  </div>
                </a>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1668757183096-bc55a8992558?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxoYXBweSUyMHBldHN8ZW58MHx8fHwxNzcwOTY0OTUyfDA&ixlib=rb-4.1.0&q=85" 
                alt="Happy pets" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1e1919] text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_26712d7a-6b6e-4dd6-88e7-f2147f408025/artifacts/zr04zw0o_%5BOriginal%20size%5D%20FurrBowl%20%281000%20x%201000%20px%29%20%284000%20x%204000%20px%29%20%281000%20x%20500%20px%29%20%2815%20x%2015%20cm%29%20%28567%20x%20567%20px%29%20%282%29.png" 
                alt="FurrBowl Logo" 
                className="h-20 w-auto mb-4"
              />
              <p className="text-gray-400 text-sm">
                Fresh, homemade, and natural food for your beloved pets.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => scrollToSection('home')} className="hover:text-white transition-colors">Home</button></li>
                <li><button onClick={() => scrollToSection('menu')} className="hover:text-white transition-colors">Menu</button></li>
                <li><button onClick={() => scrollToSection('plans')} className="hover:text-white transition-colors">Plans</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors">Services</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Order Now</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="https://www.swiggy.com/menu/1200378?source=sharing" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    Swiggy
                  </a>
                </li>
                <li>
                  <a href="https://zomato.onelink.me/xqzv/fx595vjb" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    Zomato
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Phone: 8050539777</li>
                <li>
                  <a href="https://www.instagram.com/furrbowl" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    Instagram: @furrbowl
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2024 FurrBowl. All rights reserved. Nurturing bonds with love and care.</p>
          </div>
        </div>
      </footer>

      {/* Cart Drawer */}
      <CartDrawer />
    </div>
  );
};

export default LandingPage;
